/* PB Form sub-panel — Postal Bill of Export-II per invoice; pick AD code + customs broker, freight shared with EDF. */
import { DocAcksBanner } from "@/components/DocAcksBanner";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Eye, FileCheck2, Loader2, Plus, Search, Trash2, Upload } from "lucide-react";
import { openPdf } from "@/components/EdfDocPanel";
import { BrokerManager } from "@/components/BrokerManager";
import { BankSelect } from "@/components/BankSelect";

const H = () => ({ headers: getAuthHeader() });
const STATUS = {
  needs_bank: ["Pick AD code", "bg-amber-100 text-amber-800"],
  needs_broker: ["Pick broker", "bg-violet-100 text-violet-800"],
  needs_freight: ["Enter freight", "bg-sky-100 text-sky-800"],
  ready: ["Ready", "bg-emerald-100 text-emerald-800"],
};
const fmt = (v) => Number(v || 0).toFixed(2);

const AddBroker = ({ open, onClose, onAdded }) => {
  const [f, setF] = useState({ name: "", licence_no: "", address: "" });
  const save = async () => {
    try {
      const r = await axios.post(`${API}/pb-doc/brokers`, f, H());
      toast.success(`Broker ${r.data.name} added`); setF({ name: "", licence_no: "", address: "" }); onAdded(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add broker"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pb-broker-dialog">
        <DialogHeader><DialogTitle className="text-base">Add customs broker</DialogTitle></DialogHeader>
        <Input placeholder="Name (e.g. CROSS WATER LOGISTICS)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pb-broker-name" />
        <Input placeholder="Licence No. (e.g. 11/2440)" value={f.licence_no} onChange={(e) => setF({ ...f, licence_no: e.target.value })} data-testid="pb-broker-licence" />
        <Input placeholder="Address (optional)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="pb-broker-address" />
        <Button onClick={save} disabled={!f.name.trim() || !f.licence_no.trim()} data-testid="pb-broker-save">Add broker</Button>
      </DialogContent>
    </Dialog>
  );
};

const PbRow = ({ r, banks, brokers, isAdmin, onSaved, onDelete, onAddBroker, onBankAdded }) => {
  const [fr, setFr] = useState(r.freight_set ? String(r.freight_usd) : "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFr(r.freight_set ? String(r.freight_usd) : ""); }, [r.freight_set, r.freight_usd]);
  const save = async (patch) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/pb-doc/history/${r.id}`, patch, H());
      onSaved(res.data);
      if (res.data.status === "ready") toast.success(`PB form ${r.invoice_no} ready`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      if ("freight_usd" in patch) setFr(r.freight_set ? String(r.freight_usd) : "");
    }
    setBusy(false);
  };
  const [lbl, cls] = STATUS[r.status] || STATUS.needs_bank;
  return (
    <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`pb-row-${r.invoice_no}`}>
      <td data-label="Invoice" className="py-2 font-mono font-semibold text-indigo-700">{r.invoice_no}</td>
      <td data-label="Date" className="text-slate-600">{r.invoice_date}</td>
      <td data-label="Buyer" className="max-w-[150px] truncate" title={`${r.buyer} · ${r.country}`}>{r.buyer}<div className="text-[10px] text-slate-400">{r.country}</div></td>
      <td data-label="Company" className="text-xs text-slate-600" data-testid={`pb-company-${r.invoice_no}`}>{r.company_name}</td>
      <td data-label="FOB ($)" className="text-right font-semibold" data-testid={`pb-fob-${r.invoice_no}`}>${fmt(r.fob_usd)}</td>
      <td data-label="Freight ($)" className="text-center">
        <Input value={fr} onChange={(e) => setFr(e.target.value)} placeholder="$"
          onBlur={() => fr.trim() !== "" && Number(fr) !== (r.freight_set ? r.freight_usd : NaN) && save({ freight_usd: fr })}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className="mx-auto h-7 w-20 text-right" data-testid={`pb-freight-${r.invoice_no}`} />
      </td>
      <td data-label="INR" className="pr-3 text-right text-xs" data-testid={`pb-inr-${r.invoice_no}`}>₹{fmt(r.fob_usd * r.rate)}<div className="text-[10px] text-slate-400">@ {fmt(r.rate)}</div></td>
      <td data-label="AD code">
        <BankSelect companyId={r.company_id} companyName={r.company_name} banks={banks} value={r.bank_id} disabled={busy}
          onPick={(id) => save({ bank_id: id })} onAdded={(b) => onBankAdded(r.company_id, b)} testid={`pb-bank-${r.invoice_no}`} />
      </td>
      <td data-label="Customs broker" className="whitespace-nowrap">
        <select value={r.broker_id || ""} disabled={busy}
          onChange={(e) => (e.target.value === "__add" ? onAddBroker(r) : save({ broker_id: e.target.value }))}
          className="h-7 w-48 rounded border px-1 text-xs" data-testid={`pb-broker-${r.invoice_no}`}>
          <option value="">— customs broker —</option>
          {brokers.map(b => <option key={b.id} value={b.id}>{b.name} · {b.licence_no}</option>)}
          <option value="__add">+ Add new broker…</option>
        </select>
      </td>
      <td data-label="Status"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`pb-status-${r.invoice_no}`}>{busy ? "Saving…" : lbl}</span></td>
      <td data-label="Actions" className="text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={r.status !== "ready"} onClick={() => openPdf(r.id, true, "pb-doc")} data-testid={`pb-view-${r.invoice_no}`}><Eye className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={r.status !== "ready"} onClick={() => openPdf(r.id, false, "pb-doc")} data-testid={`pb-dl-${r.invoice_no}`}><Download className="h-4 w-4" /></Button>
        {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => onDelete(r)} data-testid={`pb-del-${r.invoice_no}`}><Trash2 className="h-4 w-4" /></Button>}
      </td>
    </tr>
  );
};

export default function PbDocPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [banks, setBanks] = useState({});
  const [brokers, setBrokers] = useState([]);
  const [addFor, setAddFor] = useState(null);
  const [mgr, setMgr] = useState(false);
  const [q, setQ] = useState("");
  const [up, setUp] = useState(false);
  const fileRef = useRef(null);
  const loadBrokers = () => axios.get(`${API}/pb-doc/brokers`, H()).then(r => setBrokers(r.data.brokers)).catch(() => {});
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/pb-doc/history`, { ...H(), params: { q } }).catch(() => ({ data: { rows: [] } }));
    setRows(r.data.rows);
    const ids = [...new Set(r.data.rows.map(x => x.company_id).filter(Boolean))];
    const got = await Promise.all(ids.map(id => axios.get(`${API}/edf-doc/banks/${id}`, H()).then(b => [id, b.data.banks]).catch(() => [id, []])));
    setBanks(Object.fromEntries(got));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadBrokers(); }, []);
  const saved = (p) => setRows(rs => rs.map(x => (x.id === p.id ? { ...x, ...p } : x)));
  const bankAdded = (cid, b) => setBanks(m => ({ ...m, [cid]: [...(m[cid] || []), b] }));
  const upload = async (f) => {
    if (!f) return;
    setUp(true);
    const fd = new FormData(); fd.append("packing_slip", f);
    try { const r = await axios.post(`${API}/pb-doc/generate`, fd, H()); toast.success(`PB form ${r.data.invoice_no} drafted`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read this packing slip"); }
    setUp(false); if (fileRef.current) fileRef.current.value = "";
  };
  const del = async (r) => {
    if (!window.confirm(`Delete PB form ${r.invoice_no}?`)) return;
    await axios.delete(`${API}/pb-doc/history/${r.id}`, H()).catch(() => toast.error("Delete failed")); load();
  };
  const added = async (b) => {
    await loadBrokers();
    const row = addFor; setAddFor(null);
    if (row?.id) {
      const res = await axios.put(`${API}/pb-doc/history/${row.id}`, { broker_id: b.id }, H()).catch(() => null);
      if (res) saved(res.data);
    }
  };
  return (
    <div className="space-y-5" data-testid="pb-doc-panel">
      <DocAcksBanner />
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800"><FileCheck2 className="h-5 w-5 text-indigo-600" /> PB Form (Postal Bill of Export-II)</h2>
        <p className="mt-1 text-sm text-slate-500">Drafted automatically for each invoice when its packing slip is uploaded in Order Register. Pick the AD code and customs broker; freight is shared with the EDF of the same invoice.</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="pb-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={up} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pb-upload-btn">
            {up ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload packing slip
          </Button>
          <Button variant="outline" onClick={() => setAddFor({})} data-testid="pb-add-broker-btn"><Plus className="mr-1 h-4 w-4" /> Add broker</Button>
          <Button variant="outline" onClick={() => setMgr(true)} data-testid="pb-manage-brokers-btn">Manage brokers ({brokers.length})</Button>
        </div>
      </div>
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-slate-700">PB forms</h3>
          <div className="relative ml-auto w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, buyer, company" className="pl-8" data-testid="pb-search" />
          </div>
        </div>
        {rows === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="pb-empty">No PB forms yet.</p>) : (
          <div className="overflow-x-auto">
            <table className="doc-cards w-full text-sm" data-testid="pb-table">
              <thead><tr className="border-b text-left text-xs text-slate-500">
                <th className="py-2">Invoice</th><th>Date</th><th>Buyer</th><th>Company</th><th className="text-right">FOB ($)</th><th className="text-center">Freight ($)</th>
                <th className="pr-3 text-right">INR</th><th>AD code</th><th>Customs broker</th><th>Status</th><th className="text-right">Actions</th>
              </tr></thead>
              <tbody>{rows.map(r => <PbRow key={r.id} r={r} banks={banks[r.company_id] || []} brokers={brokers} isAdmin={isAdmin} onBankAdded={bankAdded}
                onSaved={saved} onDelete={del} onAddBroker={setAddFor} />)}</tbody>
            </table>
          </div>)}
      </div>
      <AddBroker open={!!addFor} onClose={() => setAddFor(null)} onAdded={added} />
      <BrokerManager open={mgr} onClose={() => setMgr(false)} brokers={brokers} isAdmin={isAdmin}
        onChanged={() => { loadBrokers(); load(); }} />
    </div>
  );
}
